<script setup lang="ts">
import { BasicButton } from '@proj-airi/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useChatConnection } from '../../../composables/use-chat-connection'

const props = withDefaults(defineProps<{
  compact?: boolean
  showOpenChat?: boolean
}>(), {
  compact: false,
  showOpenChat: false,
})

const emit = defineEmits<{
  open: [route: string]
  openChat: []
}>()

const { t } = useI18n()
const connection = useChatConnection()

const statusLabel = computed(() => t(`settings.pages.chat-connection.status.${connection.phase.value}`))
const summary = computed(() => {
  const phase = connection.phase.value
  if (phase === 'ready' || phase === 'missing-model' || phase === 'failed' || phase === 'validating') {
    return t(`settings.pages.chat-connection.summary.${phase}`, {
      provider: connection.providerLabel.value || t('settings.pages.chat-connection.unknown-provider'),
      model: connection.modelLabel.value || t('settings.pages.chat-connection.unknown-model'),
    })
  }
  return t('settings.pages.chat-connection.summary.missing-provider')
})

const actionLabel = computed(() => {
  switch (connection.phase.value) {
    case 'ready':
      return t('settings.pages.chat-connection.action.change')
    case 'missing-model':
      return t('settings.pages.chat-connection.action.select-model')
    case 'failed':
      return t('settings.pages.chat-connection.action.verify-again')
    case 'validating':
      return t('settings.pages.chat-connection.action.cancel-verification')
    case 'missing-provider':
      return t('settings.pages.chat-connection.action.configure')
  }
})

const checkedLabel = computed(() => {
  if (!connection.checkedAt.value)
    return ''
  const date = new Date(connection.checkedAt.value)
  if (Number.isNaN(date.getTime()))
    return ''
  return t('settings.pages.chat-connection.checked-at', { time: date.toLocaleString() })
})

function openConnection() {
  emit('open', connection.route.value)
}
</script>

<template>
  <section
    :data-chat-connection-phase="connection.phase.value"
    :class="[
      'flex gap-3',
      props.compact ? 'flex-row items-center' : 'flex-col',
      'rounded-xl border border-neutral-200 bg-white/95 p-3',
      'dark:border-neutral-700 dark:bg-neutral-900/95',
    ]"
  >
    <div :class="['min-w-0 flex-1']">
      <p :class="['text-sm font-medium text-neutral-800 dark:text-neutral-100']">
        {{ statusLabel }}
      </p>
      <p :class="['text-sm text-neutral-600 dark:text-neutral-300']">
        {{ summary }}
      </p>
      <p
        v-if="connection.errorText.value"
        role="alert"
        :class="['mt-1 text-sm text-red-700 dark:text-red-300']"
      >
        {{ connection.errorText.value }}
      </p>
      <p v-if="checkedLabel" :class="['mt-1 text-xs text-neutral-500']">
        {{ checkedLabel }}
      </p>
    </div>
    <div :class="['flex shrink-0 gap-2']">
      <BasicButton
        v-if="props.showOpenChat && connection.phase.value === 'ready'"
        size="sm"
        type="button"
        @click="emit('openChat')"
      >
        {{ t('settings.pages.chat-connection.action.open-chat') }}
      </BasicButton>
      <BasicButton size="sm" type="button" @click="openConnection">
        {{ actionLabel }}
      </BasicButton>
    </div>
  </section>
</template>
